import { useState } from 'react';
import { Icon } from '@/components/Icon';
import { ValuePreview } from '@/components/inspect/ValuePreview';
import { EmptyState, ErrorPage, Spinner } from '@/components/primitives';
import { useRedisKeys } from '@/hooks/useSturmData';
import { bytes, count, text } from '@/lib/inspectFormat';
import type { RedisDatabase } from '@/types/inspect';

const LIMITS = [25, 100, 250, 500];

export function KeyBrowser({
  keyPrefix,
  defaultDatabase,
  databases,
}: {
  keyPrefix: string;
  defaultDatabase: number;
  databases: RedisDatabase[];
}) {
  const [match, setMatch] = useState(keyPrefix ? `${keyPrefix}*` : '*');
  const [database, setDatabase] = useState(defaultDatabase);
  const [limit, setLimit] = useState(100);
  const [includeMemory, setIncludeMemory] = useState(false);
  const [applied, setApplied] = useState<{
    match: string;
    db: number;
    limit: number;
    memory: boolean;
    cursor: number;
    pageOffset: number;
  } | null>(null);
  const [selected, setSelected] = useState<string | null>(null);

  const query = applied
    ? {
        match: applied.match,
        db: applied.db,
        limit: applied.limit,
        memory: applied.memory,
        cursor: applied.cursor,
        pageOffset: applied.pageOffset,
      }
    : { cursor: 0, pageOffset: 0 };

  const { data, isFetching, isError, error } = useRedisKeys(query, applied != null);

  const scan = (cursor: number, pageOffset: number) => {
    setApplied({
      match: match.trim(),
      db: database,
      limit,
      memory: includeMemory,
      cursor,
      pageOffset,
    });
    setSelected(null);
  };

  return (
    <div>
      <div className="filterbar mb-16">
        <input
          className="input mono"
          placeholder={keyPrefix ? `${keyPrefix}*` : '*'}
          value={match}
          onChange={(e) => setMatch(e.target.value)}
        />
        <select
          className="select"
          value={database}
          onChange={(e) => setDatabase(Number(e.target.value))}
        >
          {(databases.length > 0
            ? databases.map((d) => d.index)
            : [defaultDatabase]
          ).map((index) => (
            <option key={index} value={index}>
              db {index}
            </option>
          ))}
        </select>
        <select className="select" value={limit} onChange={(e) => setLimit(Number(e.target.value))}>
          {LIMITS.map((value) => (
            <option key={value} value={value}>
              {value} keys
            </option>
          ))}
        </select>
        <label className="row gap-8 tiny muted" style={{ cursor: 'pointer' }}>
          <input
            type="checkbox"
            checked={includeMemory}
            onChange={(e) => setIncludeMemory(e.target.checked)}
          />
          Include memory usage
        </label>
        <button className="btn btn-secondary btn-sm" onClick={() => scan(0, 0)} disabled={isFetching}>
          {isFetching ? <Spinner size={12} /> : <Icon name="search" size={12} />} Scan
        </button>
      </div>

      {isError ? (
        <ErrorPage
          title="Could not scan the keyspace"
          message={error instanceof Error ? error.message : 'Redis did not respond.'}
        />
      ) : null}

      {applied == null ? (
        <EmptyState
          title="Nothing scanned yet"
          sub="Scanning is an explicit action: it runs SCAN against a shared instance, so it never happens on page load."
        />
      ) : null}

      {applied != null && !isError && data?.keys.length === 0 ? (
        <EmptyState
          title={`No keys match ${data.match}`}
          sub={`No key in db ${data.database} matches this pattern. The Databases tab lists which databases hold keys.`}
        />
      ) : null}

      {data && data.keys.length > 0 ? (
        <div className="grid-2-1">
          <div className="card">
            <table className="tbl">
              <thead>
                <tr>
                  <th>Key</th>
                  <th>Type</th>
                  <th>TTL</th>
                  <th>Length</th>
                  {applied?.memory ? <th>Memory</th> : null}
                </tr>
              </thead>
              <tbody>
                {data.keys.map((item) => (
                  <tr
                    key={item.key}
                    className="clickable"
                    onClick={() => setSelected(item.key)}
                    style={selected === item.key ? { background: 'var(--ink-50)' } : undefined}
                  >
                    <td className="mono" style={{ wordBreak: 'break-all' }}>
                      {item.key}
                    </td>
                    <td>
                      <span className="badge">{item.type}</span>
                    </td>
                    <td className="tbl-num">{item.ttl == null ? 'none' : item.ttl}</td>
                    <td className="tbl-num">{count(item.length)}</td>
                    {applied?.memory ? (
                      <td className="tbl-num">
                        {item.memoryBytes == null ? '—' : bytes(item.memoryBytes)}
                      </td>
                    ) : null}
                  </tr>
                ))}
              </tbody>
            </table>

            <div className="card-pad row gap-8">
              <span className="tiny muted flex1">
                Pattern <span className="mono">{text(data.match)}</span> · scanned{' '}
                {count(data.scanned)} key(s) in {count(data.iterations)} iteration(s)
              </span>
              <button
                className="btn btn-secondary btn-sm"
                onClick={() => scan(data.nextCursor ?? 0, data.nextPageOffset)}
                disabled={isFetching || data.nextCursor == null}
              >
                <Icon name="arrowRight" size={12} /> Load more
              </button>
            </div>
          </div>

          <ValuePreview redisKey={selected} database={data.database} />
        </div>
      ) : null}
    </div>
  );
}
